// Socket Configuration
const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';

const normalizeSocketUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.protocol === 'ws:') {
      parsedUrl.protocol = 'http:';
    } else if (parsedUrl.protocol === 'wss:') {
      parsedUrl.protocol = 'https:';
    }

    return parsedUrl.toString().replace(/\/$/, '');
  } catch {
    return url;
  }
};

export const SocketConfig = {
  URL: normalizeSocketUrl(socketUrl),
  OPTIONS: {
    transports: ['polling', 'websocket'], // Prefer polling first for broader compatibility
    timeout: 30000, // Increased timeout for QA environment
    forceNew: false,
    autoConnect: true,
    upgrade: true,
    // Add retry configuration for better reliability
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5,
  },
};

export const ApiEndpoints = {
  REGISTER: '/auth/register',
  LOGIN: '/auth/login',
  CREATE_LOGO: '/profile/logo/create',
  FETCH_SUBSCRIPTION: '/subscription/list',
  VERIFY_SUBSCRIPTION: '/subscription/verify-subscription',
  CREATE_SUBSCRIPTION: '/subscription/create-subscription',
  UPDATE_PAYMENT_METHOD: '/subscription/update-payment-method',
  VERIFY_PROMOCODE: '/subscription/verify-promo-code',
  FETCH_STATES: '/profile/states',
  GET_STATE_CITY_POSTAL: '/profile/getStateCityPostal',
  CREATE_ADDRESS: '/profile/addresses',
  FETCH_ADDRESS: '/profile/addresses',
  UPDATE_STORE_DETAILS: '/profile/store-details',
  FETCH_TAX_RATES: '/onboarding/taxRates/details',
  UPSERT_TAX_RATES: '/onboarding/taxRates/upsert',
  CHANGE_PASSWORD: '/auth/change-password',
  FETCH_ADDONS: '/onboarding/addOn/list',
  UPSERT_ADDONS: '/onboarding/addOn/upsert',
  DELETE_ADDONS: '/onboarding/addOn/delete',
  RESTORE_ADDONS: '/onboarding/addOn/restore',
  FETCH_COLORS: '/color/list',
  UPSERT_COLORS: '/color/upsert',
  DELETE_COLORS: '/color/delete',
  RESTORE_COLORS: '/color/restore',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VALIDATE_TOKEN: '/auth/validate-token',
  FETCH_PRODUCTS: '/onboarding/product/list',
  FETCH_MINTYPE: '/onboarding/productMinType/list',
  UPSERT_PRODUCTS: '/onboarding/product/upsert',
  DELETE_PRODUCTS: '/onboarding/product/delete',
  RESTORE_PRODUCTS: '/onboarding/product/restore',
  UPSERT_MINTYPE: '/onboarding/productMinType/upsert',
  UPSERT_FEETYPE: '/onboarding/serviceFeeType/upsert',
  FETCH_FEETYPE: '/onboarding/serviceFeeType/list',
  DELETE_FEETYPE: '/onboarding/serviceFeeType/delete',
  UPDATE_FEETYPE: '/onboarding/serviceFeeType/update',
  RESTORE_FEETYPE: '/onboarding/serviceFeeType/restore',
  FETCH_SPECIES: '/species/list',
  CREATE_SPECIES: '/species/create',
  FETCH_PACKAGE_SIZE: '/onboarding/packageSize/list',
  CREATE_PACKAGE_SIZE: '/onboarding/packageSize/upsert',
  DELETE_PACKAGE_SIZE: '/onboarding/packageSize/delete',
  FETCH_PACKAGE_TYPE: '/onboarding/packageType/list',
  CREATE_PACKAGE_TYPE: '/onboarding/packageType/upsert',
  DELETE_PACKAGE_TYPE: '/onboarding/packageType/delete',
  RESTORE_PACKAGE_TYPE: '/onboarding/packageType/restore',
  RESTORE_PACKAGE_FEETYPE: '/onboarding/packageFeeType/restore',
  FETCH_ROLES: '/roles/list',
  FETCH_MODULES: '/modules/list',
  UPSERT_ROLE: '/roles/upsert',
  DELETE_USER_ROLE: '/user/roles/delete',
  UPSERT_USER_ROLE: '/user/roles/upsert',
  FETCH_USER_ROLES: '/user/roles/list',
  FETCH_SERVICE: '/onboarding/service/list',
  UPSERT_SERVICE: '/onboarding/service/upsert',
  DELETE_SERVICE: '/onboarding/service/delete',
  RESTORE_SERVICE: '/onboarding/service/restore',
  FETCH_SERVICE_RATE: '/onboarding/serviceRate/list',
  CREATE_SERVICE_RATE: '/onboarding/serviceRate/create',
  UPDATE_SERVICE_RATE: '/onboarding/serviceRate/update',
  DELETE_SERVICE_RATE: '/onboarding/serviceRate/delete',
  RESTORE_SERVICE_RATE: '/onboarding/serviceRate/restore',
  FETCH_MIN_QUANTITY: '/onboarding/productMinQuantity/list',
  UPSERT_PRODUCT_MIN_QUANTITY: '/onboarding/productMinQuantity/upsert',
  FETCH_OPERATION: '/onboarding/operations/list',
  FETCH_OPERATIONS_MASTERS: '/onboarding/operations/masters',
  CREATE_OPERATION: '/onboarding/operations/upsert',
  DELETE_OPERATION: '/onboarding/operations/delete',
  FETCH_CUSTOMERS: '/butcher/customer/list',
  UPSERT_CUSTOMER: '/butcher/customer/upsert',
  UPSERT_USER: '/user/roles/upsert',
  FETCH_PACKAGE_FEETYPE: '/onboarding/packageFeeType/list',
  UPSERT_PACKAGE_FEETYPE: '/onboarding/packageFeeType/upsert',
  DELETE_PACKAGE_FEETYPE: '/onboarding/packageFeeType/delete',
  FETCH_SPECIES_MASTERDATA: '/butcher/order/masters',
  ORDER_SPECIES_VALIDATION: '/butcher/order/species-validation',
  CREATE_ORDER: '/butcher/order/create-order',
  EDIT_ORDER: '/butcher/order/edit-order',
  ALL_ORDERS: '/butcher/order/list',
  ORDER_DETAILS: '/butcher/order/detail',
  ORDER_GENERATE_PDF: '/butcher/order/generate-pdf',
  SERVICE_PROCESS_TASK_DETAILS: '/butcher/serviceProcess/list',
  SERVICE_PROCESS_WORKFLOW: '/butcher/serviceProcess/workflow',
  SERVICE_PROCESS_UPSERT_NOTES: '/butcher/serviceProcess/notes',
  SERVICE_PROCESS_RESTORE: '/butcher/serviceProcess/reopen',
  CUSTOMER_ORDER_HISTORY: '/butcher/order/list',
  PRODUCT_PROCESSING_TASK_LIST: '/butcher/productProcess/list',
  PRODUCT_PROCESSING_TASK_DETAILS: '/butcher/productProcess/detail',
  PRODUCT_PROCESS_WORKFLOW: '/butcher/productProcess/workflow',
  PRODUCT_PROCESS_UPSERT_NOTES: '/butcher/productProcess/note',
  PRODUCT_PROCESS_RESTORE: '/butcher/productProcess/reopen',
  INVOICE_LIST: '/butcher/invoiceProcess/list',
  INVOICE_DETAILS: '/butcher/invoiceProcess/detail',
  PICKUP_LIST: '/butcher/pickupProcess/list',
  INVOICE_GENERATION: '/butcher/invoiceProcess/create',
  ORDER_ALL_INVOICES: '/butcher/invoiceProcess/invoiceList',
  ORDER_INVOICE_VIEW: '/butcher/invoiceProcess/invoiceDetail',
  MARK_AS_PAID: '/butcher/invoiceProcess/markAsPaid',
  RESEND_NOTIFICATION: '/butcher/invoiceProcess/resendNotification',
  MARK_AS_PICKEDUP: '/butcher/pickupProcess/markAsPickedUp',
  PRODUCT_ORDER_LIST: '/butcher/order/productOrderlist',
  SERVICE_ORDER_LIST: '/butcher/order/serviceOrderlist',
  STORAGE_COST_LIST: '/onboarding/storageCost/list',
  STORAGE_COST_UPSET: '/onboarding/storageCost/upsert',
  OPERATIONS_RESET: '/onboarding/operations/resetDefault',
  OPERATION_DELETE: '/onboarding/operations/delete',
  DELETE_ROLE: '/roles/delete',
  CUSTOMER_LICENSE_VALIDATE: '/butcher/customer/validateLicense',
  FETCH_CUSTOMER_LICENSE_DETAILS: '/butcher/customer/listLicenses',
  CUSTOMER_LICENSE_UPSERT: '/butcher/customer/upsertLicense',
  UPDATE_ONBOARDING_STEP: '/onboarding/updateOnBoardStep',
  DELETE_SERVICE_PROCESS_NOTES: '/butcher/serviceProcess/deleteNote',
  DELETE_PRODUCT_PROCESS_NOTES: '/butcher/productProcess/deleteNote',
  USER_PROFILE_IMAGE_UPLOAD: '/profile/user-logo',
  FETCH_DASHBOARD: '/dashboard/data',
  FETCH_BUTCHER_SUBSCRIPTION_DETAILS: '/subscription/get-subscription-details',
  REACTIVATE_SUBSCRIPTION: '/subscription/reactivate-subscription',
  CANCEL_SUBSCRIPTION: '/subscription/cancel-subscription',
  FETCH_PAYMENT_HISTORY: '/subscription/payment-history',
  CONTACT_US: '/contactUs',
  CONTACT_US_IMAGE_UPLOAD: '/contactUs/image/create',
};

export const FrontendRoutes = {
  REGISTRATION: '/registration',
  LOGIN: '/login',
  RESET_PASSWORD: '/reset/:token',
  INVITE_USER: '/invite/:token',
  FORGOT_PASSWORD: '/forgot-password',

  // Subscriptions
  VIEW_PLANS: '/view/plan',
  VIEW_PLAN_DETAILS: '/view/plan/details',
  SUBSCRIPTION_CONFIRMATION: '/subscription/confirmation',
  SUBSCRIPTION_FAILED: '/subscription/failed',
  PAYMENT_METHOD_UPDATE_SUCCESS: '/payment-method/success',
  PAYMENT_METHOD_UPDATE_FAILED: '/payment-method/failed',

  // App Settings
  APP_SETTINGS: '/app-settings',
  ABOUT_APPLICATION: '/about-application',
  HELP_FAQ: '/help-faq',
  SUBSCRIPTION_DETAILS: '/subscription/details',
  SUBSCRIPTION_BILLING_DETAILS: '/subscription/details/billing',
  SUBSCRIPTION_BILLING_INVOICE_DETAILS: '/subscription/details/billing/invoice',
  SUBSCRIPTION_REACTIVATE: '/subscription/reactivate',
  SUBSCRIPTION_REACTIVATE_PLANS_DETAILS: '/subscription/reactivate/plans',
  SUBSCRIPTION_REACTIVATE_SUCCESS: '/subscription/reactivate/success',

  // Onboarding routes
  LOGO: '/profile/logo',
  ADDRESS: '/profile/address',
  TAX_DETAILS: '/profile/tax-details',
  PROFILE_DETAILS: '/profile/details',
  DASHBOARD: '/dashboard',
  SPECIES: '/species',
  SERVICES: '/species/services',
  COLOR: '/species/color',
  FEETYPE: '/species/fee-type',
  SPECIES_AND_SERVICE: '/species/species-service',
  PRODUCTS: '/products',
  ADDONS: '/products/addons',
  PACKAGING: '/packaging',
  PACKAGING_SIZE: '/products/packaging-size',
  PACKAGING_TYPE: '/products/packaging-type',
  ROLE_MANAGEMENT: '/role-management',
  USER_MANAGEMENT: '/user-management',
  OPERATION_MANAGEMENT: '/operation-management',
  STORAGE_COST: '/storage-cost',

  // Customer
  CUSTOMER_LIST: '/customer/list',
  CUSTOMER_DETAILS: '/customer/details/:customerId',
  CREATE_CUSTOMER: '/customer/create/',
  UPDATE_CUSTOMER: '/customer/update/:customerId',

  // Create order routes below
  CREATE_ORDER: '/create-order/',
  CREATE_ORDER_SPECIES_CUSTOMISATION: '/create-order/species/customise/',
  CREATE_ORDER_SPECIES_SELECTION: '/create-order/species/select/',
  CREATE_ORDER_CHECKIN: '/create-order/check-in',
  CREATE_ORDER_PRODUCT: '/create-order/product',
  CREATE_ORDER_SERVICE: '/create-order/service',
  CREATE_ORDER_PACKAGING: '/create-order/packaging',
  CREATE_ORDER_PRODUCT_DETAILS: '/create-order/product/details',
  SPECIES_SUMMARY: '/create-order/species/summary',
  CREATE_ORDER_SUMMARY: '/create-order/order/summary',
  CART: 'cart',
  CREATE_ORDER_CONFIRMATION: '/create-order/order/confirmation',
  CREATE_ORDER_VIEW_SUMMARY: '/create-order/order/view/summary',
  ORDER_HISTORY: '/customer/order-history',
  CREATE_ORDER_SPECIES_CUSTOMISATION_V2: 'species/customize',

  // All Order
  ALL_ORDER_LIST: '/all-order/list',
  ORDER_DETAILS: '/order/details/',

  // Service Processing
  SERVICE_PROCESSING_LIST: '/service-processing/list',
  SERVICE_PROCESS_TASK_LIST: '/service-processing/task/list',
  SERVICE_PROCESS_TASK_DETAILS: '/service-processing/task/details',
  SERVICE_PROCESS_SERVICE_DETAILS: '/service-processing/service/details',
  PRODUCT_PROCESSING_LIST: '/product-processing/list',
  PRODUCT_PROCESSING_TASK_LIST: '/product-processing/task/list',
  PRODUCT_PROCESSING_TASK_DETAILS: '/product-processing/task/details',
  PRODUCT_INPROGRESS_DETAILS: '/product-processing/task/inprogress/details',
  PRODUCT_COMPLETED_DETAILS: '/product-processing/task/completed/details',

  // Invoice
  INVOICE_LIST: '/invoice/list',
  INVOICE_ORDERED_ITEMS: '/invoice/ordereditems',
  INVOICE_DETAILS: '/invoice/details',
  ALL_INVOICES: '/order/invoice',

  //Pick up
  ORDER_PICKUP_LIST: '/pickup/list',

  // Notifications
  NOTIFICATION: '/all-notifications',

  // My Store settings
  STORE_LOGO: '/store/logo',
  STORE_ADDRESS: '/store/address',
  STORE_TAX: '/store/tax',
};

export const onboardingSteps = {
  USER_MANAGEMENT: 'User Role Management',
  ROLE_MANAGEMENT: 'Role Management',
  OPERATION_MANAGEMENT: 'Operation',
};
